import { router } from "@inertiajs/react";
import { Users, User } from "lucide-react";
import { useState, useEffect } from "react";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { SectionCard } from "../../components/SectionCard";
import { WorkspaceCard } from "../../components/WorkspaceCard";
import { EmptyState } from "../../components/ui/EmptyState";
import { WorkspaceCardSkeleton } from "../../components/ui/WorkspaceCardSkeleton";
import TeamWorkspaceModal from "../../components/TeamWorkspaceModal";

interface Workspace {
  id: number;
  name: string;
  slug: string;
  workspace_type: "personal" | "team";
  role: "owner" | "admin" | "member";
  member_count: number;
  aws_accounts_count: number;
  github_repositories_count: number;
}

interface WorkspaceIndexProps {
  workspaces: Workspace[];
}

export default function WorkspaceIndex({ workspaces }: WorkspaceIndexProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isCreatingPersonal, setIsCreatingPersonal] = useState(false);

  useEffect(() => {
    const startHandler = () => setIsLoading(true);
    const finishHandler = () => setIsLoading(false);

    const unsubscribeStart = router.on('start', startHandler);
    const unsubscribeFinish = router.on('finish', finishHandler);

    return () => {
      unsubscribeStart();
      unsubscribeFinish();
    };
  }, []);

  const personalWorkspaces = workspaces.filter(
    (w) => w.workspace_type === "personal",
  );
  const teamWorkspaces = workspaces.filter((w) => w.workspace_type === "team");

  const createPersonalWorkspace = () => {
    setIsCreatingPersonal(true);

    router.post('/workspaces', {
      workspace_type: 'personal'
    }, {
      onFinish: () => {
        setIsCreatingPersonal(false);
      }
    });
  };

  return (
    <Layout
      title="Workspaces"
      description="Manage your personal and team workspaces"
    >
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={[{ label: "Workspaces" }]} />
          <PageHeader title="Workspaces" />

          {/* Personal Workspace Section */}
          <SectionCard title="Personal workspace" icon={User} className="mb-6">
            {isLoading ? (
              <WorkspaceCardSkeleton />
            ) : personalWorkspaces.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {personalWorkspaces.map((workspace) => (
                  <WorkspaceCard key={workspace.id} workspace={workspace} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={User}
                title="You don't have a personal workspace yet"
                description="Create your personal workspace to manage your cloud accounts and infrastructure."
                actionText={isCreatingPersonal ? "Creating..." : "Create personal workspace"}
                onAction={createPersonalWorkspace}
                actionDisabled={isCreatingPersonal}
              />
            )}
          </SectionCard>

          {/* Team Workspaces Section */}
          <SectionCard
            title="Team workspaces"
            icon={Users}
            className="mb-6"
            action={
              teamWorkspaces.length > 0 ? (
                <button
                  onClick={() => setIsTeamModalOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors"
                >
                  Create team workspace
                </button>
              ) : undefined
            }
          >
            {isLoading ? (
              <div className="space-y-3">
                <WorkspaceCardSkeleton />
                <WorkspaceCardSkeleton />
              </div>
            ) : teamWorkspaces.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {teamWorkspaces.map((workspace) => (
                  <WorkspaceCard key={workspace.id} workspace={workspace} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Users}
                title="You don't have any team workspaces yet"
                description="Create a team workspace to collaborate with others, share resources, and manage access together."
                actionText="Create team workspace"
                onAction={() => setIsTeamModalOpen(true)}
              />
            )}
          </SectionCard>
        </div>
      </section>

      <TeamWorkspaceModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
      />
    </Layout>
  );
}
